import { ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ModuleStatusBadge } from './module-status-badge';

import type { TrainingModuleSummary } from '@/hooks/use-training-modules';
import type { EnrichedModuleProgress } from '@/hooks/use-training-progress';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatDate, formatPercentage } from '@/lib/format';



interface ModuleCardProps {
  module: TrainingModuleSummary;
  progress: EnrichedModuleProgress | undefined;
}

export function ModuleCard({ module, progress }: ModuleCardProps) {
  const [open, setOpen] = useState(false);
  const status = progress?.status ?? 'not_started';
  const isCompleted = status === 'completed';

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              Module {module.order_index}
            </span>
            <ModuleStatusBadge status={status} />
          </div>
          <h3 className="text-lg font-semibold">{module.title}</h3>
          <p className="text-sm text-muted-foreground">{module.description}</p>
        </div>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          ~{module.estimated_minutes} min
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isCompleted && progress?.completed_at && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Completed {formatDate(progress.completed_at)}</span>
            {progress.score_percentage != null && (
              <span>Score: {formatPercentage(progress.score_percentage)}</span>
            )}
          </div>
        )}
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1 p-0 text-sm">
              Learning objectives
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
              {module.learning_objectives?.map((obj, i) => (
                <li key={i}>{obj}</li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
        <Button asChild variant={isCompleted ? 'outline' : 'default'}>
          <Link to={`/training/modules/${module.id}`}>
            {isCompleted ? 'Review' : status === 'in_progress' ? 'Continue' : 'Start Module'}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
